import * as tslib_1 from "tslib";
import { Logger } from "@pnp/logging";
import { CachingParserWrapper, ODataBatch } from "@pnp/odata";
import { ClientSvcQueryable } from "./clintsvcqueryable";
import { ObjectPath, opSetId, opSetParentId, opSetPathId, opSetPathParamId } from "./objectpath";
import { objectPath } from "./opactionbuilders";
import { staticMethod } from "./opbuilders";
import { ProcessQueryParser } from "./parsers";
import { writeObjectPathBody } from "./utils";
/**
 * Implements ODataBatch for use with the ObjectPath framework
 */
var ObjectPathBatch = /** @class */ (function (_super) {
    tslib_1.__extends(ObjectPathBatch, _super);
    function ObjectPathBatch(parentUrl, _batchId) {
        var _this = _super.call(this, _batchId) || this;
        _this.parentUrl = parentUrl;
        return _this;
    }
    ObjectPathBatch.prototype.executeImpl = function () {
        // if we don't have any requests, don't bother sending anything
        // this could be due to caching further upstream, or just an empty batch
        if (this.requests.length < 1) {
            Logger.write("Resolving empty batch.", 1 /* Info */);
            return Promise.resolve();
        }
        var executor = new BatchExecutor(this.parentUrl, this.batchId);
        executor.appendRequests(this.requests);
        return executor.execute();
    };
    return ObjectPathBatch;
}(ODataBatch));
export { ObjectPathBatch };
var BatchExecutor = /** @class */ (function (_super) {
    tslib_1.__extends(BatchExecutor, _super);
    function BatchExecutor(parentUrl, batchId) {
        var _this = _super.call(this, parentUrl) || this;
        _this.batchId = batchId;
        _this._requests = [];
        _this._builderIndex = 1;
        // we add our session object path and hard code in the IDs so we can reference it
        var method = staticMethod("GetTaxonomySession", "{981cbc68-9edc-4f8d-872f-71146fcbb84f}");
        method.path = opSetId("0", method.path);
        method.actions.push(opSetId("1", opSetPathId("0", objectPath())));
        _this._objectPaths.add(method);
        return _this;
    }
    BatchExecutor.prototype.appendRequests = function (requests) {
        var _this = this;
        requests.forEach(function (request) {
            // grab the special property we added to options when we created the batch info
            var pathQueue = request.options.clientsvc_ObjectPaths;
            var paths = pathQueue.toArray();
            // getChildRelationships
            if (paths.length < 0) {
                return;
            }
            var indexMappingFunction = function (n) { return n; };
            if (/GetTaxonomySession/i.test(paths[0].path)) {
                // drop the first thing as it is a get session object path, which we add once for the entire batch
                paths = paths.slice(1);
                // replace the next item's parent id with 0, which will be the id of the session call at the root of this request
                paths[0].path = opSetParentId("0", paths[0].path);
                indexMappingFunction = function (n) { return n - 1; };
            }
            var lastOpId = -1;
            var idIndexMap = [];
            paths.map(function (op, index, arr) {
                // rewrite the path string
                var opId = ++_this._builderIndex;
                // track the array index => opId relationship
                idIndexMap.push(opId);
                var path = opSetPathParamId(idIndexMap, opSetId(opId.toString(), op.path), indexMappingFunction);
                if (lastOpId >= 0) {
                    path = opSetParentId(lastOpId.toString(), path);
                }
                // rewrite actions with placeholders replaced
                var opActions = op.actions.map(function (a) {
                    var actionId = ++_this._builderIndex;
                    return opSetId(actionId.toString(), opSetPathId(opId.toString(), a));
                });
                // handle any specific child relationships
                // the childIndex is reduced by 1 because we are removing the Session Path
                pathQueue.getChildRelationship(index + 1).map(function (i) { return i - 1; }).forEach(function (childIndex) {
                    // set the parent id for our non-immediate children
                    arr[childIndex].path = opSetParentId(opId.toString(), arr[childIndex].path);
                });
                // and remember our last object path id for the parent replace above
                lastOpId = opId;
                // return our now substituted path and actions as a new object path instance
                return new ObjectPath(path, opActions);
            }).forEach(function (op) { return _this._objectPaths.add(op); });
            // get this once
            var obPaths = _this._objectPaths.toArray();
            // create a new parser to handle finding the result based on the path
            var parser = new ProcessQueryParser(obPaths[obPaths.length - 1]);
            if (request.parser instanceof CachingParserWrapper) {
                // handle special case of caching
                request.parser = new ProcessQueryCachingParserWrapper(parser, request.parser);
            }
            else {
                request.parser = parser;
            }
            // add the request to our batch requests
            _this._requests.push(request);
            // remove the temp property
            delete request.options.clientsvc_ObjectPaths;
        });
    };
    BatchExecutor.prototype.execute = function () {
        var _this = this;
        Logger.write("[" + this.batchId + "] (" + (new Date()).getTime() + ") Executing batch with " + this._requests.length + " requests.", 1 /* Info */);
        // create our request body from all the merged object paths
        var options = {
            body: writeObjectPathBody(this._objectPaths.toArray()),
        };
        Logger.write("[" + this.batchId + "] (" + (new Date()).getTime() + ") Sending batch request.", 1 /* Info */);
        // send the batch
        return _super.prototype.postCore.call(this, options, new BatchParser()).then(function (rawResponse) {
            Logger.write("[" + _this.batchId + "] (" + (new Date()).getTime() + ") Resolving batched requests.", 1 /* Info */);
            return _this._requests.reduce(function (chain, request) {
                Logger.write("[" + request.id + "] (" + (new Date()).getTime() + ") Resolving request in batch " + _this.batchId + ".", 1 /* Info */);
                return chain.then(function (_) { return request.parser.findResult(rawResponse).then(request.resolve).catch(request.reject); });
            }, Promise.resolve());
        });
    };
    return BatchExecutor;
}(ClientSvcQueryable));
/**
 * Used to return the raw results from parsing the batch
 */
var BatchParser = /** @class */ (function (_super) {
    tslib_1.__extends(BatchParser, _super);
    function BatchParser() {
        return _super.call(this, null) || this;
    }
    BatchParser.prototype.findResult = function (json) {
        // we leave it to the individual request parsers to find their results in the raw json body
        return json;
    };
    return BatchParser;
}(ProcessQueryParser));
/**
 * Handles processing batched results that are also cached
 */
var ProcessQueryCachingParserWrapper = /** @class */ (function (_super) {
    tslib_1.__extends(ProcessQueryCachingParserWrapper, _super);
    function ProcessQueryCachingParserWrapper(parser, wrapper) {
        return _super.call(this, parser, wrapper.cacheOptions) || this;
    }
    ProcessQueryCachingParserWrapper.prototype.findResult = function (json) {
        var _this = this;
        return this.parser.findResult(json).then(function (d) { return _this.cacheData(d); });
    };
    return ProcessQueryCachingParserWrapper;
}(CachingParserWrapper));
//# sourceMappingURL=batch.js.map